import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Banknote } from "lucide-react";
import toast from "react-hot-toast";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/utils/cn";
import { formatDateDmy } from "@/utils/helper";
import {
  useMarkItemsPaidMutation,
  type IBankTransferItem,
} from "@/services/(company)/payrollRunsApi";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const safeDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return formatDateDmy(d);
};

const maskAccount = (acc: string | null) => {
  if (!acc) return null;
  if (acc.length <= 4) return acc;
  return `****${acc.slice(-4)}`;
};

interface IProps {
  runId: number;
  items?: IBankTransferItem[];
  isLoading?: boolean;
}

export const BankTransferCard = ({ runId, items = [], isLoading }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const unpaid = useMemo(() => items.filter((i) => !i.paid), [items]);
  const unpaidIds = useMemo(() => unpaid.map((i) => i.id), [unpaid]);
  const paidCount = items.length - unpaid.length;

  const currencyTotals = useMemo(() => {
    const map = new Map<string, { symbol: string; total: number }>();
    for (const item of items) {
      const code = item.currency.code;
      const prev = map.get(code) ?? { symbol: item.currency.symbol, total: 0 };
      map.set(code, { ...prev, total: prev.total + toNumber(item.netPay) });
    }
    return [...map.entries()].map(([code, v]) => ({ code, total: v.total }));
  }, [items]);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [note, setNote] = useState("");
  const [markPaid, { isLoading: isSaving }] = useMarkItemsPaidMutation();

  const allSelected =
    unpaidIds.length > 0 && unpaidIds.every((id) => selected.has(id));

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(unpaidIds));
  };

  const handleSubmit = async (itemIds: number[]) => {
    if (itemIds.length === 0) return;
    try {
      await markPaid({
        runId,
        itemIds,
        ...(note.trim() ? { note: note.trim() } : {}),
      }).unwrap();
      toast.success(
        itemIds.length === 1
          ? t("company:payrolls.marked_as_paid")
          : t("company:payrolls.marked_as_paid_count", {
              count: itemIds.length,
            }),
      );
      setSelected(new Set());
      setNote("");
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? err?.message ?? t("company:payrolls.failed_to_mark_paid")),
      );
    }
  };

  const handleMarkAll = () => {
    if (unpaidIds.length === 0) return;
    handleSubmit(unpaidIds);
  };

  return (
    <div className="relative rounded-sm border border-borderSecondary bg-white flex flex-col overflow-hidden">
      {/* top accent bar */}
      <span className="absolute inset-x-0 top-0 h-[3px] bg-primary" />

      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4 border-b border-borderSecondary">
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-sm flex items-center justify-center bg-primary/10">
            <Banknote size={14} className="text-primary" />
          </div>
          <span className="text-sm font-bold text-gray-900">
            {t("company:payrolls.bank_transfer")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {unpaid.length > 0 && (
            <>
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="edit"
                mode="hide"
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={handleMarkAll}
                disabled={isSaving}
              >
                <Check size={13} />
                {t("company:payrolls.mark_all_paid_count", {
                  count: unpaid.length,
                })}
              </PermissionButton>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                {t("company:payrolls.select_all_unpaid")}
              </label>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col flex-1 max-h-120 overflow-y-auto">
        {isLoading && items.length === 0 ? (
          <div className="px-5 py-8 text-xs text-gray-400 text-center">
            {t("company:payrolls.loading_bank_transfer_items")}
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-8 text-xs text-gray-400 text-center">
            {t("company:payrolls.no_bank_transfer_items_in_this_run")}
          </div>
        ) : (
          items.map((r) => {
            const amount = formatAmount(toNumber(r.netPay));
            const paidAt = safeDate(r.paidAt);
            const isSelected = selected.has(r.id);
            const acc = maskAccount(r.accountNumber);
            return (
              <div
                key={r.id}
                className={cn(
                  "flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/70 transition-colors",
                  isSelected && "bg-primary/5",
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {!r.paid && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(r.id)}
                    />
                  )}
                  <Avatar className="size-9">
                    {r.employee.profileImage && (
                      <AvatarImage
                        src={r.employee.profileImage}
                        alt={r.employee.fullName}
                      />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                      {getInitials(r.employee.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-gray-900 truncate">
                      {r.employee.fullName}
                    </span>
                    <span className="text-[11px] text-gray-400 truncate">
                      {r.employee.employeeCode}
                      {r.department?.name && ` · ${r.department.name}`}
                    </span>
                    {r.bankName && (
                      <span className="text-[11px] text-gray-500 truncate">
                        {r.bankName}
                        {acc ? ` · ${acc}` : ""}
                      </span>
                    )}
                    {r.paid && paidAt && (
                      <span className="text-[11px] text-success truncate">
                        {t("company:payrolls.paid_on_date", { date: paidAt })}
                        {r.paidNote ? ` · ${r.paidNote}` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-bold text-gray-900 tabular-nums">
                    {r.currency.code} {amount}
                  </span>
                  {r.paid ? (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-success/10 text-success px-2.5 py-1 text-xs font-semibold">
                      <Check size={12} />
                      {t("company:payrolls.paid")}
                    </span>
                  ) : (
                    <PermissionButton
                      section={PLATFORM_SECTION_ENUM.PAYROLL}
                      action="edit"
                      mode="hide"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 !text-[11px] gap-1"
                      onClick={() => handleSubmit([r.id])}
                      disabled={isSaving}
                    >
                      <Check size={11} />
                      {t("company:payrolls.mark_as_paid")}
                    </PermissionButton>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {items.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-5 py-2.5 bg-muted/40 border-t border-borderSecondary">
          <div className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground font-medium">
              {t("company:payrolls.employee_count", { count: items.length })}
            </span>
            {paidCount > 0 && (
              <span className="text-success font-medium">
                {t("company:payrolls.paid_count", { count: paidCount })}
              </span>
            )}
            {unpaid.length > 0 && (
              <span className="text-warning-foreground font-medium">
                {t("company:payrolls.pending_count", { count: unpaid.length })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-muted-foreground">
              {t("company:payrolls.total")}
            </span>
            <span className="font-bold text-foreground tabular-nums">
              {currencyTotals
                .map((c) => `${c.code} ${formatAmount(c.total)}`)
                .join("  ·  ")}
            </span>
          </div>
        </div>
      )}

      {unpaid.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-5 py-3 bg-muted/40 border-t border-borderSecondary mt-auto">
          <span className="text-[11px] text-gray-500">
            {selected.size > 0
              ? t("common:table.selected", { count: selected.size })
              : t("company:payrolls.select_employees_to_bulk_mark_paid")}
          </span>
          <PermissionButton
            section={PLATFORM_SECTION_ENUM.PAYROLL}
            action="edit"
            mode="hide"
            type="button"
            variant="primary"
            size="sm"
            onClick={() => handleSubmit(Array.from(selected))}
            disabled={selected.size === 0 || isSaving}
          >
            {isSaving
              ? t("common:states.saving")
              : selected.size > 0
                ? t("company:payrolls.mark_paid_count", {
                    count: selected.size,
                  })
                : t("company:payrolls.mark_paid_no_count")}
          </PermissionButton>
        </div>
      )}
    </div>
  );
};
